import React, { useState } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
} from '@mui/material';

// Import Sample Data Badge
import { SampleDataBadge } from '../../common';

// Expand icon
const ExpandIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path
            d="M4 16V12M4 16H8M4 16L9 11M16 4V8M16 4H12M16 4L11 9"
            stroke="#666666"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

/**
 * All TL Stats Table
 * Shows statistics for all Tinkerers Labs with pagination
 */
const MBFAllTLStatsTable = ({ data = [], isSampleData = false, onMaximize, isFullscreen = false }) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    // Default sample data matching the Figma design
    const defaultData = [
        { name: 'IIT BOMBAY', users: 1200, adminUsers: 49, inventoryIssues: 12, inventoryPurchases: '$1,049.14', machineBooking: '$110.20', issuesReported: 3, issuesSolved: 2, projectsUndergoing: 16 },
        { name: 'IIT DELHI', users: 400, adminUsers: 55, inventoryIssues: 12, inventoryPurchases: '$1,049.14', machineBooking: '$115.50', issuesReported: 8, issuesSolved: 5, projectsUndergoing: 24 },
        { name: 'IIT GUWAHATI', users: 1500, adminUsers: 34, inventoryIssues: 12, inventoryPurchases: '$1,049.14', machineBooking: '$156.96', issuesReported: 9, issuesSolved: 6, projectsUndergoing: 31 },
        { name: 'IIT JAMMU', users: 600, adminUsers: 12, inventoryIssues: 73, inventoryPurchases: '$194.49', machineBooking: '$69.46', issuesReported: 11, issuesSolved: 11, projectsUndergoing: 21 },
        { name: 'IIT MADRAS', users: 1600, adminUsers: 30, inventoryIssues: 15, inventoryPurchases: '$190.65', machineBooking: '$247.49', issuesReported: 4, issuesSolved: 1, projectsUndergoing: 6 },
        { name: 'IIT KANPUR', users: 1900, adminUsers: 15, inventoryIssues: 12, inventoryPurchases: '$104.94', machineBooking: '$177.04', issuesReported: 6, issuesSolved: 0, projectsUndergoing: 9 },
        { name: 'IIT INDORE', users: 2000, adminUsers: 45, inventoryIssues: 42, inventoryPurchases: '$115.33', machineBooking: '$195.97', issuesReported: 16, issuesSolved: 12, projectsUndergoing: 19 },
        { name: 'IIT ROORKEE', users: 1100, adminUsers: 28, inventoryIssues: 18, inventoryPurchases: '$220.45', machineBooking: '$135.20', issuesReported: 7, issuesSolved: 4, projectsUndergoing: 14 },
        { name: 'IIT KHARAGPUR', users: 1800, adminUsers: 52, inventoryIssues: 25, inventoryPurchases: '$310.22', machineBooking: '$185.30', issuesReported: 12, issuesSolved: 9, projectsUndergoing: 28 },
        { name: 'IIT HYDERABAD', users: 900, adminUsers: 22, inventoryIssues: 8, inventoryPurchases: '$145.60', machineBooking: '$98.75', issuesReported: 5, issuesSolved: 3, projectsUndergoing: 11 },
    ];

    // Transform API data to component format
    // API returns snake_case, component expects camelCase
    const tableData = data.length > 0
        ? data.map(item => ({
            name: item.name_of_tl || item.name || 'N/A',
            users: item.no_of_users ?? item.users ?? 0,
            adminUsers: item.admin_users ?? item.adminUsers ?? 0,
            inventoryIssues: item.inventory_issues ?? item.inventoryIssues ?? 0,
            inventoryPurchases: item.inventory_purchases || item.inventoryPurchases || '$0.00',
            machineBooking: item.machine_booking_hrs ?? item.machineBooking ?? 0,
            issuesReported: item.issues_reported ?? item.issuesReported ?? 0,
            issuesSolved: item.issues_solved ?? item.issuesSolved ?? 0,
            projectsUndergoing: item.projects_undergoing ?? item.projectsUndergoing ?? 0
        }))
        : defaultData;

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Paginated data
    const paginatedData = tableData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    // Fullscreen mode - render just the table without wrapper
    if (isFullscreen) {
        return (
            <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <TableContainer sx={{ flex: 1 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                                <TableCell sx={{ fontWeight: 600, color: '#666', fontSize: '12px', py: 1.5, whiteSpace: 'nowrap' }}>
                                    Name of TL
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#666', fontSize: '12px', py: 1.5, whiteSpace: 'nowrap' }}>
                                    No. of users
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#666', fontSize: '12px', py: 1.5, whiteSpace: 'nowrap' }}>
                                    Admin users
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#666', fontSize: '12px', py: 1.5, whiteSpace: 'nowrap' }}>
                                    Inventory issues
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#666', fontSize: '12px', py: 1.5, whiteSpace: 'nowrap' }}>
                                    Inventory Purchases
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#666', fontSize: '12px', py: 1.5, whiteSpace: 'nowrap' }}>
                                    Machine Booking (Hrs)
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#666', fontSize: '12px', py: 1.5, whiteSpace: 'nowrap' }}>
                                    Issues reported
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#666', fontSize: '12px', py: 1.5, whiteSpace: 'nowrap' }}>
                                    Issues solved
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#666', fontSize: '12px', py: 1.5, whiteSpace: 'nowrap' }}>
                                    Projects undergoing
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedData.map((row, index) => (
                                <TableRow
                                    key={index}
                                    sx={{
                                        '&:hover': { bgcolor: '#f9f9f9' },
                                        borderBottom: '1px solid #f0f0f0',
                                    }}
                                >
                                    <TableCell sx={{ fontSize: '13px', color: '#333', py: 2, fontWeight: 500 }}>
                                        {row.name}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '13px', color: '#666', py: 2 }}>
                                        {(row.users || 0).toLocaleString()}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '13px', color: '#666', py: 2 }}>
                                        {(row.adminUsers || 0).toLocaleString()}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '13px', color: '#666', py: 2 }}>
                                        {(row.inventoryIssues || 0).toLocaleString()}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '13px', color: '#666', py: 2 }}>
                                        {row.inventoryPurchases}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '13px', color: '#666', py: 2 }}>
                                        {(row.machineBooking || 0).toLocaleString()}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '13px', color: '#666', py: 2 }}>
                                        {(row.issuesReported || 0).toLocaleString()}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '13px', color: '#666', py: 2 }}>
                                        {(row.issuesSolved || 0).toLocaleString()}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '13px', color: '#666', py: 2 }}>
                                        {(row.projectsUndergoing || 0).toLocaleString()}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={tableData.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25]}
                    sx={{
                        borderTop: '1px solid #e6e6e6',
                        '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                            fontSize: '12px',
                        },
                        '.MuiTablePagination-select': {
                            fontSize: '12px',
                        },
                    }}
                />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                bgcolor: '#ffffff',
                borderRadius: '12px',
                border: '1px solid #e6e6e6',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
                overflow: 'hidden',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    bgcolor: '#ffffff',
                    borderBottom: '1px solid #e6e6e6',
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexShrink: 0,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography
                        sx={{
                            color: '#333333',
                            fontFamily: '"Roboto", sans-serif',
                            fontSize: '16px',
                            lineHeight: '20px',
                            fontWeight: 500,
                        }}
                    >
                        All TL Stats
                    </Typography>
                    {isSampleData && <SampleDataBadge absolute={false} />}
                </Box>

                <IconButton
                    size="small"
                    onClick={onMaximize}
                    sx={{
                        bgcolor: '#ffffff',
                        border: '1px solid #e6e6e6',
                        borderRadius: '8px',
                        padding: '6px',
                        '&:hover': { bgcolor: '#f9f9f9' },
                    }}
                >
                    <ExpandIcon />
                </IconButton>
            </Box>

            {/* Table */}
            <TableContainer sx={{ flex: 1 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                            <TableCell sx={{ fontWeight: 600, color: '#666', fontSize: '12px', py: 1.5, whiteSpace: 'nowrap' }}>
                                Name of TL
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#666', fontSize: '12px', py: 1.5, whiteSpace: 'nowrap' }}>
                                No. of users
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#666', fontSize: '12px', py: 1.5, whiteSpace: 'nowrap' }}>
                                Admin users
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#666', fontSize: '12px', py: 1.5, whiteSpace: 'nowrap' }}>
                                Inventory issues
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#666', fontSize: '12px', py: 1.5, whiteSpace: 'nowrap' }}>
                                Inventory Purchases
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#666', fontSize: '12px', py: 1.5, whiteSpace: 'nowrap' }}>
                                Machine Booking (Hrs)
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#666', fontSize: '12px', py: 1.5, whiteSpace: 'nowrap' }}>
                                Issues reported
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#666', fontSize: '12px', py: 1.5, whiteSpace: 'nowrap' }}>
                                Issues solved
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#666', fontSize: '12px', py: 1.5, whiteSpace: 'nowrap' }}>
                                Projects undergoing
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedData.map((row, index) => (
                            <TableRow
                                key={index}
                                sx={{
                                    '&:hover': { bgcolor: '#f9f9f9' },
                                    borderBottom: '1px solid #f0f0f0',
                                }}
                            >
                                <TableCell sx={{ fontSize: '13px', color: '#333', py: 2, fontWeight: 500 }}>
                                    {row.name}
                                </TableCell>
                                <TableCell sx={{ fontSize: '13px', color: '#666', py: 2 }}>
                                    {(row.users || 0).toLocaleString()}
                                </TableCell>
                                <TableCell sx={{ fontSize: '13px', color: '#666', py: 2 }}>
                                    {(row.adminUsers || 0).toLocaleString()}
                                </TableCell>
                                <TableCell sx={{ fontSize: '13px', color: '#666', py: 2 }}>
                                    {(row.inventoryIssues || 0).toLocaleString()}
                                </TableCell>
                                <TableCell sx={{ fontSize: '13px', color: '#666', py: 2 }}>
                                    {row.inventoryPurchases}
                                </TableCell>
                                <TableCell sx={{ fontSize: '13px', color: '#666', py: 2 }}>
                                    {(row.machineBooking || 0).toLocaleString()}
                                </TableCell>
                                <TableCell sx={{ fontSize: '13px', color: '#666', py: 2 }}>
                                    {(row.issuesReported || 0).toLocaleString()}
                                </TableCell>
                                <TableCell sx={{ fontSize: '13px', color: '#666', py: 2 }}>
                                    {(row.issuesSolved || 0).toLocaleString()}
                                </TableCell>
                                <TableCell sx={{ fontSize: '13px', color: '#666', py: 2 }}>
                                    {(row.projectsUndergoing || 0).toLocaleString()}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
                component="div"
                count={tableData.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25]}
                sx={{
                    borderTop: '1px solid #e6e6e6',
                    '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                        fontSize: '12px',
                    },
                    '.MuiTablePagination-select': {
                        fontSize: '12px',
                    },
                }}
            />
        </Box>
    );
};

export default MBFAllTLStatsTable;
